"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-[13px] font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 rounded-full bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full border-b border-primary/10 bg-background/95 p-6 shadow-2xl backdrop-blur-xl md:hidden">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} onClick={onClose} className="block border-b border-primary/5 pb-4 text-lg font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary">
              {item.label}
            </Link>
          </div>)}
        <div className="pt-4">
          <Button asChild className="w-full rounded-full border border-primary/50 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground shadow-lg shadow-primary/10" size="lg">
            <Link to="/ContactPage" onClick={onClose}>
              Rezervace
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
interface MobileMenuToggleProps {
  isOpen: boolean;
  onToggle: () => void;
}
function MobileMenuToggle({
  isOpen,
  onToggle
}: MobileMenuToggleProps) {
  return <Button variant="ghost" size="icon" className="inline-flex text-foreground md:hidden" onClick={onToggle} aria-expanded={isOpen} aria-label="Toggle navigation menu">
      {isOpen ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
    </Button>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="224841" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "border-b border-primary/10 bg-background/80 backdrop-blur-lg" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="47">
        <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center">
            <span className="font-serif text-2xl tracking-widest uppercase text-foreground">sensu</span>
          </Link>

          <div className="flex flex-1 items-center justify-center">
            <DesktopNav />
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <Button asChild className="hidden rounded-full border border-primary/40 bg-transparent text-primary transition-all duration-500 hover:bg-primary hover:text-primary-foreground hover:shadow-lg hover:shadow-primary/20 md:inline-flex">
              <Link to="/ContactPage">Rezervace</Link>
            </Button>

            <MobileMenuToggle isOpen={isMobileMenuOpen} onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
          </div>
        </div>

        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}